import type { TFunction } from '@mint-lib/i18n';
import type { NavigateOptions } from '@mint/routing';

import type {
  DataFileClientStepNames,
  DataFileServerStepNames,
} from '../api/types.js';
import { importHubPaths } from '../hooks/useImportHubRouteApi.js';

type StepConfig<T extends string = DataFileClientStepNames> = {
  name: T;
  title: string;
  index: number;
  link: NavigateOptions;
  virtual?: boolean;
  disabled?: boolean;
};

type StepsConfigArray<T extends string = DataFileClientStepNames> = Array<
  StepConfig<T>
>;

type StepsConfigProxy<T extends string = DataFileClientStepNames> =
  StepsConfigArray<T> &
    Record<T, StepConfig<T>> & {
      findStep(fromStep: T, direction: number): StepConfig<T>;
      isLastStep(stepName: T): boolean;
      getVisibleSteps(): StepsConfigArray<T>;
    };

const createProxyHandler = <T extends string>(): ProxyHandler<
  StepsConfigProxy<T>
> => ({
  get: function (target, prop: string | symbol) {
    if (typeof prop === 'string' && target[prop as keyof typeof target]) {
      return target[prop as keyof typeof target];
    }

    if (typeof prop === 'string') {
      return target.find((step) => step.name === prop);
    }

    return undefined;
  },
});

const createBaseStepsConfig = <T extends string>({
  t,
  includeHeaderSelection,
  granularityLevel,
}: {
  t: TFunction;
  includeHeaderSelection: boolean;
  granularityLevel?: string | null;
}): StepsConfigProxy<T> => {
  const isPricingItemLevel = granularityLevel === 'pricing_item';
  const paths = importHubPaths;

  const _stepsConfig = [
    {
      name: 'upload',
      title: t('File import setup'),
      link: {
        to: paths.dataFileUpload,
      },
      get index() {
        return _stepsConfig.indexOf(this);
      },
    },
    ...(includeHeaderSelection
      ? [
          {
            name: 'header_selection',
            title: t('File import setup'),
            link: {
              to: paths.dataFileHeaderSelection,
            },
            virtual: true,
            get index() {
              return _stepsConfig.indexOf(this);
            },
          } as StepConfig<T>,
        ]
      : []),
    {
      name: 'headers_mapping',
      title: t('Data mapping'),
      link: {
        to: paths.dataFileHeadersMapping,
      },
      get index() {
        return _stepsConfig.indexOf(this);
      },
    },
    {
      name: 'validation',
      title: t('Data check'),
      link: {
        to: paths.dataFileValidation,
      },
      get index() {
        return _stepsConfig.indexOf(this);
      },
    },
    {
      name: 'channel_assignment',
      title: t('Data assignment'),
      link: {
        to: paths.dataFileDataAssignment,
      },
      disabled: isPricingItemLevel,
      get index(): number {
        return _stepsConfig.indexOf(this);
      },
    } as StepConfig<T>,
    {
      name: 'success',
      title: t('Success'),
      link: {
        to: paths.performance,
      },
      virtual: true,
      get index(): number {
        return _stepsConfig.indexOf(this);
      },
    } as StepConfig<T>,
  ] as StepsConfigProxy<T>;

  _stepsConfig.findStep = function (fromStep: T, direction: number) {
    const enabledSteps = this.filter((step) => !step.disabled);
    const currentIndex = enabledSteps.findIndex(
      (step) => step.name === fromStep,
    );
    return enabledSteps[currentIndex + direction];
  };

  _stepsConfig.isLastStep = function (stepName: T) {
    const visibleSteps = this.getVisibleSteps();
    const currentStepIndex = this[stepName].index;
    // Check if there's any enabled step after current step
    const hasNextEnabledStep = visibleSteps.some(
      (step) => step.index > currentStepIndex && !step.disabled,
    );
    return !hasNextEnabledStep;
  };

  _stepsConfig.getVisibleSteps = function () {
    return this.filter((step) => !step.virtual);
  };

  return new Proxy(_stepsConfig, createProxyHandler<T>());
};

export const createClientStepsConfig = ({
  t,
  granularityLevel,
}: {
  t: TFunction;
  granularityLevel?: string | null;
}): StepsConfigProxy<DataFileClientStepNames> => {
  return createBaseStepsConfig<DataFileClientStepNames>({
    t,
    includeHeaderSelection: true,
    granularityLevel,
  });
};

export const createServerStepsConfig = ({
  t,
  granularityLevel,
}: {
  t: TFunction;
  granularityLevel?: string | null;
}): StepsConfigProxy<DataFileServerStepNames> => {
  return createBaseStepsConfig<DataFileServerStepNames>({
    t,
    includeHeaderSelection: false,
    granularityLevel,
  });
};
